/**
 * Hitting somebody, on purpose.
 *
 * This is friends messing about in a dark building, so the whole design problem
 * is the accident: nobody should ever kill a brother by walking into him in a
 * doorway, and nobody should be able to claim they did not mean it. So a strike
 * is not a button press. It is a **wind-up**: you have to put the crosshair on a
 * specific person, at arm's length, and hold the key while a ring closes on
 * their chest. Look away, back off, or let go, and it comes to nothing.
 *
 * That gives four separate refusals — out of range, not aimed, not held long
 * enough, still cooling down — and every one of them is visible on screen while
 * it is happening. It also makes the hit legible to the person being hit: the
 * ring is drawn in the world, at them, so they get most of a second of warning.
 *
 * The gesture can own its own key and mouse listeners, but in this game it does
 * not: `core/input.ts` binds `attack` to V and the right mouse button like
 * every other key, and `game.ts` drives `hold()` from it. Passing `key: null,
 * mouse: false` is what turns the listeners off. They are still there for a
 * harness, or a replay, or anything else with no `InputAPI` to read.
 *
 * Damage is applied by the *server*, which range-checks it (`STRIKE_RANGE`) and
 * relays a `struck` to the victim. Nothing here touches anybody's health: a
 * client that decided how hurt other people were would be a client that could
 * decide everyone was dead.
 */

import * as THREE from 'three';
import { clamp01, lerp } from '../core/math';
// The one shared number a strike has to agree with the server about. Imported
// rather than copied: `protocol.ts` has no imports of its own, so this costs
// the render layer nothing.
import { STRIKE_RANGE } from '../net/protocol';
import type { RemotePlayers } from './remoteplayers';

export interface StrikeOptions {
  /**
   * A wind-up completed. Hand this straight to `NetClient.strike`; the server
   * decides whether it lands.
   */
  onStrike(target: string, amount: number): void;
  /**
   * Key that must be held, or null for no keyboard listener of its own — which
   * is what the game passes, because `core/input.ts` owns the binding and
   * `hold()` is fed from it.
   */
  key?: string | null;
  /** Also accept the right mouse button while the pointer is locked. */
  mouse?: boolean;
  /** Seconds the gesture must be held before it lands. */
  windUp?: number;
  /** Seconds before another strike is possible. */
  cooldown?: number;
  /** Points of health per landed hit. */
  damage?: number;
  /**
   * Metres. Short of `STRIKE_RANGE` on purpose: the server checks against the
   * attacker's *last known* position, which is up to a frame of movement behind
   * where the attacker thinks they are, and a rejected strike that looked like
   * a hit is worse than one that never started.
   */
  range?: number;
  /** Metres from the aim ray the target's chest must be. A chest, aimed at. */
  aimRadius?: number;
}

export interface StrikeGesture {
  /** Who is under the crosshair right now, or null. */
  readonly target: string | null;
  /** 0..1 wind-up. Useful for a HUD, and for a sound cue. */
  readonly charge: number;
  /**
   * Feed the gesture from somewhere else — a gamepad, a touch button, or an
   * `attack` action if `core/input.ts` ever grows one. ORed with the module's
   * own listeners.
   */
  hold(down: boolean): void;
  /**
   * Advance the gesture. `enabled` is the game's own "may the player act" —
   * false while paused, dead, mid-descent, or with no server to send it to.
   */
  update(dt: number, camera: THREE.Camera, enabled: boolean): void;
  dispose(): void;
}

const DEFAULT_KEY = 'KeyV';
const DEFAULT_WIND_UP = 0.45;
const DEFAULT_COOLDOWN = 0.9;
/**
 * Eight hits to put somebody down. Enough that killing a friend takes about ten
 * seconds of chasing them and meaning it, and far too much to happen by
 * accident.
 */
const DEFAULT_DAMAGE = 13;
const DEFAULT_AIM_RADIUS = 0.45;

/** How fast an abandoned wind-up drains, relative to how fast it fills. */
const RELEASE_SCALE = 4;

/** Ring radii, in metres, at zero charge and at full. It closes on them. */
const RING_WIDE = 0.5;
const RING_TIGHT = 0.19;
/** Metres the ring floats in front of the target, so it never z-fights the chest. */
const RING_LIFT = 0.28;
/** Seconds the impact flash takes to fade. */
const FLASH_DECAY = 0.28;

const _chest = new THREE.Vector3();
const _toCam = new THREE.Vector3();

/** Typing in a chat box must never be a punch. */
function typing(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  if ((el as HTMLElement).isContentEditable) return true;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

class Gesture implements StrikeGesture {
  target: string | null = null;
  charge = 0;

  private readonly ring: THREE.Mesh;
  private readonly geometry: THREE.RingGeometry;
  private readonly material: THREE.MeshBasicMaterial;

  private keyHeld = false;
  private mouseHeld = false;
  private externalHeld = false;
  private cooldown = 0;
  private flash = 0;

  /** Null when the gesture is driven entirely from `hold()`. */
  private readonly key: string | null;
  private readonly useMouse: boolean;
  private readonly windUp: number;
  private readonly cooldownTime: number;
  private readonly damage: number;
  private readonly range: number;
  private readonly aimRadius: number;

  constructor(
    private readonly remotes: RemotePlayers,
    private readonly opts: StrikeOptions
  ) {
    // `undefined` means "you decide"; `null` means "nothing, I will drive it".
    this.key = opts.key === undefined ? DEFAULT_KEY : opts.key;
    this.useMouse = opts.mouse ?? true;
    this.windUp = Math.max(0.05, opts.windUp ?? DEFAULT_WIND_UP);
    this.cooldownTime = opts.cooldown ?? DEFAULT_COOLDOWN;
    this.damage = opts.damage ?? DEFAULT_DAMAGE;
    this.range = opts.range ?? STRIKE_RANGE - 0.4;
    this.aimRadius = opts.aimRadius ?? DEFAULT_AIM_RADIUS;

    // A unit ring, scaled per frame. Twenty-two segments is round enough at the
    // couple of metres it is ever seen from.
    this.geometry = new THREE.RingGeometry(0.88, 1, 22);
    this.material = new THREE.MeshBasicMaterial({
      color: 0xffd9c0,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      // Depth-tested: a wall between you and them hides the ring, which is the
      // only feedback there is that a strike is possible. Aiming through
      // wallpaper gets you nothing to look at.
      depthTest: true,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      fog: false,
    });
    this.ring = new THREE.Mesh(this.geometry, this.material);
    this.ring.visible = false;
    this.ring.renderOrder = 3;
    // Parented to the avatars' own group, so wiring this up is one `add` in the
    // renderer rather than two.
    this.remotes.object.add(this.ring);

    if (this.key !== null) {
      window.addEventListener('keydown', this.onKeyDown);
      window.addEventListener('keyup', this.onKeyUp);
    }
    window.addEventListener('blur', this.onBlur);
    if (this.useMouse) {
      window.addEventListener('mousedown', this.onMouseDown);
      window.addEventListener('mouseup', this.onMouseUp);
      window.addEventListener('contextmenu', this.onContextMenu);
    }
    document.addEventListener('pointerlockchange', this.onLockChange);
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.code !== this.key || e.repeat || typing()) return;
    this.keyHeld = true;
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    if (e.code === this.key) this.keyHeld = false;
  };

  /**
   * Right button only, and only with the pointer locked — otherwise a
   * right-click on the title screen would be a punch, and the browser's own
   * menu would still be the more useful thing to have happen.
   */
  private onMouseDown = (e: MouseEvent): void => {
    if (e.button === 2 && document.pointerLockElement) this.mouseHeld = true;
  };

  private onMouseUp = (e: MouseEvent): void => {
    if (e.button === 2) this.mouseHeld = false;
  };

  private onContextMenu = (e: MouseEvent): void => {
    if (document.pointerLockElement) e.preventDefault();
  };

  private onBlur = (): void => {
    this.keyHeld = false;
    this.mouseHeld = false;
  };

  private onLockChange = (): void => {
    if (!document.pointerLockElement) this.onBlur();
  };

  hold(down: boolean): void {
    this.externalHeld = down;
  }

  update(dt: number, camera: THREE.Camera, enabled: boolean): void {
    if (this.cooldown > 0) this.cooldown = Math.max(0, this.cooldown - dt);
    if (this.flash > 0) this.flash = Math.max(0, this.flash - dt / FLASH_DECAY);

    const held = enabled && (this.keyHeld || this.mouseHeld || this.externalHeld);
    const previous = this.target;
    this.target = enabled ? this.remotes.pick(camera, this.range, this.aimRadius) : null;

    // Swapping targets mid-wind-up starts over. A strike is aimed at a person,
    // not charged in the air and then pointed at whoever is nearest.
    if (this.target !== previous) this.charge = 0;

    if (held && this.target && this.cooldown <= 0) {
      this.charge = clamp01(this.charge + dt / this.windUp);
      if (this.charge >= 1) {
        const victim = this.target;
        this.charge = 0;
        this.cooldown = this.cooldownTime;
        this.flash = 1;
        try {
          this.opts.onStrike(victim, this.damage);
        } catch {
          // The callback is somebody else's code; it must not be able to end
          // the frame. The gesture has already done its part.
        }
      }
    } else if (this.charge > 0) {
      this.charge = clamp01(this.charge - (dt / this.windUp) * RELEASE_SCALE);
    }

    this.draw(camera);
  }

  /** The ring: a target you can see closing, on them rather than on your HUD. */
  private draw(camera: THREE.Camera): void {
    if (!this.target || !this.remotes.chestOf(this.target, _chest)) {
      this.ring.visible = false;
      return;
    }

    // Floated toward the eye so it reads as a mark on them rather than a decal
    // buried in a jacket.
    _toCam.copy(camera.position).sub(_chest);
    const dist = _toCam.length() || 1e-4;
    _toCam.multiplyScalar(1 / dist);
    this.ring.position.copy(_chest).addScaledVector(_toCam, RING_LIFT);
    this.ring.quaternion.copy(camera.quaternion);

    const flash = this.flash;
    const radius = lerp(RING_WIDE, RING_TIGHT, this.charge) + flash * 0.5;
    this.ring.scale.setScalar(radius);
    // Faint whenever somebody is in reach — that is how the gesture is
    // discovered at all — and bright only while it is actually being wound up.
    const idle = this.cooldown > 0 ? 0.06 : 0.16;
    this.material.opacity = clamp01(idle + this.charge * 0.75 + flash * 0.8);
    this.material.color.setHex(this.charge > 0.6 || flash > 0 ? 0xffe6d2 : 0xffd9c0);
    this.ring.visible = true;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('contextmenu', this.onContextMenu);
    document.removeEventListener('pointerlockchange', this.onLockChange);
    this.ring.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();
  }
}

/**
 * Build the strike gesture. It parents its own feedback to `remotes.object`, so
 * the caller adds one thing to the scene and calls one `update` a frame.
 */
export function makeStrikeGesture(remotes: RemotePlayers, opts: StrikeOptions): StrikeGesture {
  return new Gesture(remotes, opts);
}
